import React from "react";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import {
  FieldControl,
  FieldControlValue
} from "@/utils/fieldRendering/FieldControl";
import { SingleSelectOption } from "@/components/controls/SingleSelect";
import {
  FieldType,
  ResponseType,
  DATE_FIELD_BADGE_CLASSNAME,
  FIELD_CLASSNAME,
  FOCUS_CLASSNAME,
  HALF_WRAPPER_CLASSNAME
} from "@/utils/fieldRendering/shared";
import { getParsedFieldValue } from "@/utils/entityFieldsUtils/shared";
import {
  decodeCheckbox,
  encodeCheckbox
} from "@/utils/fieldRendering/legacyBoolTokens";
import {
  parseFileFieldValue,
  serializeFileFieldValue
} from "@/utils/entityFieldsUtils/fileFieldUtils";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import {
  DealQuestionnaireQuestionSettings,
  Responses
} from "@/pages/deals/types";
import DreamerIndication from "@/components/controls/Questions/DreamerIndication/DreamerIndication";
import QuestionContainer from "@/components/shared/QuestionContainer";
import { i18n, TFunction } from "i18next";

export {
  DATE_FIELD_BADGE_CLASSNAME,
  FIELD_CLASSNAME,
  FOCUS_CLASSNAME,
  HALF_WRAPPER_CLASSNAME
};

interface QuestionWrapperProps {
  question: DealQuestionnaireQuestionSettings;
  isStakeholdersApproval?: boolean;
  dealId: string;
  questionnaireId: string;
  localResponses: Responses;
  children: React.ReactNode;
}

export const QuestionWrapper: React.FC<QuestionWrapperProps> = ({
  question,
  dealId,
  questionnaireId,
  localResponses,
  children
}) => (
  <QuestionContainer
    label={question.questionText}
    isMandatory={question.isMandatory}
    tooltipText={question.tooltipText}
    indicator={
      <DreamerIndication
        dealId={dealId}
        questionnaireId={questionnaireId}
        questionId={question.id}
        questionResponse={localResponses[question.id]}
      />
    }
  >
    {children}
  </QuestionContainer>
);

interface QuestionRendererProps {
  t: TFunction<"translation", undefined>;
  question: DealQuestionnaireQuestionSettings;
  localResponses: Responses;
  updateQuestionnaireResponse: (questionId: string, newResponse: any) => void;
  dealId: string;
  questionnaireId: string;
}

const getQuestionType = (
  question: DealQuestionnaireQuestionSettings
): FieldType =>
  (question.responseType?.type as FieldType) || FieldDisplayType.TEXT;

// Persists in the deal-questionnaire wire shape; encoding stays here.
const createResponseWriter = ({
  question,
  localResponses,
  updateQuestionnaireResponse
}: Pick<
  QuestionRendererProps,
  "question" | "localResponses" | "updateQuestionnaireResponse"
>) => {
  return (value: unknown) => {
    const existingResponse = localResponses[question.id] || {};
    updateQuestionnaireResponse(question.id, {
      ...existingResponse,
      response: { value }
    });
  };
};

// `decode` maps the stored value in, `encode` maps the control's value back.
const QuestionField: React.FC<
  QuestionRendererProps & {
    decode?: (stored: unknown) => FieldControlValue;
    encode?: (value: FieldControlValue) => unknown;
    name?: string;
    showNumberHints?: boolean;
    dateVariant?: "badge" | "field";
    options?: SingleSelectOption[];
  }
> = ({
  question,
  localResponses,
  updateQuestionnaireResponse,
  dealId,
  questionnaireId,
  decode,
  encode,
  name,
  showNumberHints,
  dateVariant,
  options
}) => {
  const questionType = getQuestionType(question);
  const stored = localResponses[question.id]?.response?.value;
  const writeResponse = createResponseWriter({
    question,
    localResponses,
    updateQuestionnaireResponse
  });

  return (
    <QuestionWrapper
      question={question}
      dealId={dealId}
      questionnaireId={questionnaireId}
      localResponses={localResponses}
    >
      <FieldControl
        fieldType={questionType}
        config={question.responseType as ResponseType}
        value={
          decode
            ? decode(stored)
            : (getParsedFieldValue(stored, questionType) as FieldControlValue)
        }
        onChange={(value) => writeResponse(encode ? encode(value) : value)}
        name={name ?? `question-${question.id}`}
        options={options ?? question.responseType?.options}
        checkboxLabel={question.questionText}
        showNumberHints={showNumberHints}
        dateVariant={dateVariant}
      />
    </QuestionWrapper>
  );
};

// Date ranges are stored as a JSON string on deal questionnaires.
const decodeDateRange = (stored: unknown): FieldControlValue => {
  if (typeof stored !== "string") return undefined;
  try {
    return JSON.parse(stored);
  } catch {
    return undefined;
  }
};

const ChecklistItemField: React.FC<QuestionRendererProps> = ({
  question,
  localResponses,
  updateQuestionnaireResponse
}) => {
  const stored = localResponses[question.id]?.response?.value;
  const writeResponse = createResponseWriter({
    question,
    localResponses,
    updateQuestionnaireResponse
  });

  return (
    <FieldControl
      fieldType={FieldDisplayType.CHECKLIST_ITEM}
      value={decodeCheckbox(stored)}
      onChange={(value) => writeResponse(encodeCheckbox(value))}
      name={`checklist-item-${question.id}`}
      checkboxLabel={question.questionText}
    />
  );
};

export const renderQuestionByType = (props: QuestionRendererProps) => {
  const questionType = getQuestionType(props.question);

  switch (questionType) {
    case FieldDisplayType.NUMBER:
    case FieldDisplayType.PERCENTAGE:
    case FieldDisplayType.CURRENCY:
      return <QuestionField {...props} showNumberHints />;

    case FieldDisplayType.DATE:
      return (
        <QuestionField
          {...props}
          name={`question-date-${props.question.id}`}
          dateVariant="field"
        />
      );

    case FieldDisplayType.DATE_RANGE:
      return (
        <QuestionField
          {...props}
          name={`daterange-${props.question.id}`}
          decode={decodeDateRange}
          encode={(value) => JSON.stringify(value)}
        />
      );

    case FieldDisplayType.FILE_UPLOAD:
      return (
        <QuestionField
          {...props}
          name={`fileupload-${props.question.id}`}
          decode={(stored) => parseFileFieldValue(stored)}
          encode={(value) =>
            serializeFileFieldValue(
              Array.isArray(value) ? (value as SavedAttachmentInfo[]) : []
            )
          }
        />
      );

    case FieldDisplayType.SWITCH:
      return (
        <QuestionField
          {...props}
          decode={(stored) => stored as FieldControlValue}
        />
      );

    case FieldDisplayType.CHECKBOX:
      return (
        <QuestionField
          {...props}
          decode={decodeCheckbox}
          encode={encodeCheckbox}
        />
      );

    case FieldDisplayType.CHECKLIST_ITEM:
      return <ChecklistItemField {...props} />;

    // text, email, textarea and url need no encoding of their own.
    default:
      return <QuestionField {...props} />;
  }
};

export const renderSectionTitle = (
  i18nObj: i18n,
  section: string,
  t: TFunction<"translation", undefined>
) =>
  section === "null"
    ? t("general")
    : i18nObj.exists(`deals.dealView.sections.${section}`)
      ? t(`deals.dealView.sections.${section}`)
      : section;
